import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GST_RATE_PERCENT, canPostJournals } from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useOrgSettings } from '../features/settings/org';
import { useSession } from '../features/auth/session';
import { needsStepUp, trpc } from '../lib/trpc';

export const Route = createFileRoute('/settings/tax')({
  component: TaxPage,
});

/**
 * The agency's GST registration — one setting, for the whole organisation.
 *
 * WHY IT LIVES HERE and not on a property. Registration is a fact about the business: an agency
 * is registered or it is not, and the answer cannot differ between two houses on the same
 * street. Held per property it would be a contradiction waiting to be stored — half a portfolio
 * charging owners 15% the agency does not owe IRD — and it would surface at a GST return, months
 * later, as money already taken from people who trusted the statement.
 *
 * What IS per property is the fee RATE, because that genuinely is negotiated owner by owner.
 */
function TaxPage() {
  const org = useOrgSettings();
  const session = useSession();
  const mayChange = canPostJournals(session?.roles ?? []);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Tax"
        subtitle={org.data ? org.data.name : 'GST registration and what it applies to'}
      />

      <SettingsNav />

      {org.isError ? (
        <LoadFailed retry={() => void org.refetch()} retrying={org.isFetching} />
      ) : null}

      {org.data ? <GstPanel settings={org.data} mayChange={mayChange} /> : null}
    </div>
  );
}

function GstPanel({
  settings,
  mayChange,
}: {
  settings: { gstRegistered: boolean; gstNumber: string | null };
  mayChange: boolean;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <Panel className="p-0">
      <PanelHeader
        title="GST registration"
        subtitle="Whether management fees carry GST"
        className="px-5 pt-5 pb-2"
        aside={
          mayChange ? (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Change
            </Button>
          ) : null
        }
      />

      <div className="border-t border-rule px-5 py-4">
        <p className="flex flex-wrap items-center gap-2">
          {settings.gstRegistered ? (
            <>
              <Chip tone="positive">Registered</Chip>
              <span className="text-[0.8125rem] text-ink-muted">
                Every management fee has {GST_RATE_PERCENT}% added on top.
              </span>
            </>
          ) : (
            <>
              <Chip tone="neutral">Not registered</Chip>
              <span className="text-[0.8125rem] text-ink-muted">
                Management fees are charged with no GST.
              </span>
            </>
          )}
        </p>

        {settings.gstRegistered ? (
          <dl className="mt-3 text-[0.8125rem]">
            <dt className="text-ink-muted">GST number</dt>
            <dd className="font-semibold tabular-nums">{settings.gstNumber ?? '—'}</dd>
          </dl>
        ) : null}

        {/*
         * The asymmetry that makes this setting matter at all, said plainly. A property manager
         * who wonders why rent has no GST on it and the fee does should find the answer on the
         * screen that controls it, not in a tax guide.
         */}
        <p className="mt-4 max-w-prose text-[0.75rem] text-ink-subtle">
          Residential rent is an <strong className="font-semibold">exempt supply</strong> in New
          Zealand, so no GST is ever charged on rent itself. The agency’s management fee is an
          ordinary taxable supply, so GST applies to it whenever the agency is registered. That is
          why this setting changes the fee and never the rent.
        </p>

        <p className="mt-2 max-w-prose text-[0.75rem] text-ink-subtle">
          Changing it changes what the next receipt charges. Journals already posted carry their own
          figures and are never recomputed — GST taken in March stays what it was.
        </p>
      </div>

      {editing ? <SetGstDialog settings={settings} onClose={() => setEditing(false)} /> : null}
    </Panel>
  );
}

function SetGstDialog({
  settings,
  onClose,
}: {
  settings: { gstRegistered: boolean; gstNumber: string | null };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [registered, setRegistered] = useState(settings.gstRegistered);
  const [number, setNumber] = useState(settings.gstNumber ?? '');

  const set = useMutation(
    trpc.org.setGstRegistration.mutationOptions({
      meta: { errorTitle: 'Could not change the GST registration' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.org.settings.queryKey() });
        onClose();
      },
      onError(error) {
        // Fifteen minutes since the last code. Not something to explain in a red box.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    set.mutate({ registered, gstNumber: number.trim() === '' ? null : number.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Panel className="w-full max-w-md p-5">
        <PanelHeader
          title="GST registration"
          subtitle="Applies to every property this agency manages"
          className="p-0 pb-3"
        />

        <form onSubmit={submit} noValidate className="flex flex-col gap-3">
          <Field label="This agency is" required>
            {(props) => (
              <Select
                {...props}
                value={registered ? 'yes' : 'no'}
                onChange={(e) => setRegistered(e.target.value === 'yes')}
              >
                <option value="yes">Registered for GST — fees carry {GST_RATE_PERCENT}%</option>
                <option value="no">Not registered — fees carry no GST</option>
              </Select>
            )}
          </Field>

          {registered ? (
            <Field label="GST number" hint="As IRD wrote it. Appears on tax invoices.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  placeholder="123-456-789"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  className="tabular-nums"
                />
              )}
            </Field>
          ) : (
            <p className="rounded-md bg-surface-sunk px-3 py-2 text-[0.8125rem] text-ink-muted">
              Any GST number on file is cleared. An unregistered agency must not be putting one on
              an invoice.
            </p>
          )}

          <p className="text-[0.75rem] text-ink-subtle">
            This changes what every future rent receipt takes off every owner in the agency. It is
            recorded in the audit log with who changed it and when.
          </p>

          <div className="flex gap-2">
            <Button type="submit" disabled={set.isPending}>
              {set.isPending ? 'Saving' : 'Save'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
